import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { leadService } from '../services/api';
import { Spinner } from '../components/Spinner';
import { DrillDownPanel } from '../components/reports/DrillDownPanel';
import { useDrillDown } from '../hooks/useDrillDown';
import { ClockIcon } from '@heroicons/react/24/outline';
import { RangePicker, RangeNote, BLANK_RANGE_FILTERS, drillDates } from '../components/reports/ReportRange';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

// Only totalLeads and totalOverdues have an exact matching filter on the main
// leads list (overdue=true). The follow-up-count and day-bucket columns are
// informational only — no single-param filter reproduces them exactly.
const DrillTrigger = ({ count, active, onToggle, drillable, className = '' }) => {
  if (!count || !drillable) return <td className={`px-4 py-3 text-gray-700 ${className}`}>{count || 0}</td>;
  return (
    <td className={`px-4 py-3 ${className}`}>
      <button onClick={onToggle} className={`font-medium hover:underline text-primary-600 ${active ? 'underline' : ''}`}>{count}</button>
    </td>
  );
};

export const TATReport = () => {
  const [filters, setFilters] = useState(BLANK_RANGE_FILTERS);
  const [applied, setApplied] = useState(BLANK_RANGE_FILTERS);

  const { data, isLoading } = useQuery(
    ['tatReport', applied],
    () => leadService.getTATReport(applied),
    { keepPreviousData: true }
  );

  // The window the API actually used — drives the note and the drill-downs.
  const appliedRange = data?.data?.data?.range;

  const drill = useDrillDown(leadService.getLeads);
  const { expanded, close, search, setSearch, leads, total, loading: drillLoading, loadingMore, hasMore, loadMore } = drill;

  const toggle = (key, title, params) => {
    drill.toggle(key, title, { ...drillDates(appliedRange), ...params });
  };

  const rows = data?.data?.data?.rows || [];
  const byFollowupCategory = data?.data?.data?.byFollowupCategory || [];
  const firstUpdate = data?.data?.data?.firstUpdate || [];

  const chartData = rows.slice(0, 10).map(r => ({
    name: r.user ? `${r.user.firstName} ${r.user.lastName}` : 'Unassigned',
    '1 Day': r.day1,
    '2 Days': r.day2,
    '3-5 Days': r.day3to5,
    '6+ Days': r.day6plus,
  }));

  const panelProps = expanded ? {
    title: expanded.title,
    leads,
    total,
    isLoading: drillLoading,
    onClose: close,
    search, onSearchChange: setSearch,
    hasMore, onLoadMore: loadMore, loadingMore,
  } : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ClockIcon className="h-6 w-6 text-primary-600" />
          TAT Report
        </h1>
        <p className="mt-1 text-gray-600">Turnaround-time analysis — follow-up counts and overdue ageing, per team member.</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 mb-4 px-5 py-4 flex flex-wrap items-center gap-2">
        <RangePicker value={filters} onChange={setFilters} />
        <button onClick={() => { setApplied(filters); close(); }} className="btn-primary text-sm">Search</button>
        <button onClick={() => { setFilters(BLANK_RANGE_FILTERS); setApplied(BLANK_RANGE_FILTERS); close(); }} className="btn-outline-secondary text-sm">Clear Filter</button>
        <div className="w-full"><RangeNote applied={appliedRange} /></div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">No data for this period.</div>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-5 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Overdue ageing by team member</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="1 Day" stackId="a" fill="#fbbf24" />
                <Bar dataKey="2 Days" stackId="a" fill="#f59e0b" />
                <Bar dataKey="3-5 Days" stackId="a" fill="#f97316" />
                <Bar dataKey="6+ Days" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Leads Category against No. of Follow-ups — cross-tab, whole scope */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Leads Category Against No. of Follow-ups</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Follow-ups</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Converted</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">WIP</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byFollowupCategory.map(r => (
                    <tr key={r.bucket} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{r.bucket}</td>
                      <td className="px-4 py-3 text-emerald-700">{r.converted}</td>
                      <td className="px-4 py-3 text-red-600">{r.lost}</td>
                      <td className="px-4 py-3 text-gray-700">{r.wip}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">A follow-up is counted whenever the lead's status/sub-status changes.</div>
          </div>

          {/* Leads Updation (Based on First Update Time) */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Leads Updation (Based on First Update Time)</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Sales Executive</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Within 30 Mins</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">30-60 Mins</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">1-2 Hrs</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">2-24 Hrs</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">1-2 Days</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">2+ Days</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">No Updates</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {firstUpdate.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">No data.</td></tr>
                  ) : firstUpdate.map(r => {
                    const userName = r.user ? `${r.user.firstName} ${r.user.lastName}` : 'Unassigned';
                    return (
                      <tr key={r.userId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{userName}</td>
                        <td className="px-4 py-3 text-gray-700">{r.within30Min}</td>
                        <td className="px-4 py-3 text-gray-700">{r.upto60Min}</td>
                        <td className="px-4 py-3 text-gray-700">{r.upto2Hrs}</td>
                        <td className="px-4 py-3 text-gray-700">{r.upto24Hrs}</td>
                        <td className="px-4 py-3 text-gray-700">{r.upto2Days}</td>
                        <td className="px-4 py-3 text-orange-600">{r.over2Days}</td>
                        <td className="px-4 py-3 text-red-600">{r.noUpdate}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Follow-up count table */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Assigned Leads Against No. of Follow-ups</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Sales Executive</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">No Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">1st Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">2nd Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">3+ Follow-ups</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Total Leads</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => {
                    const rowKey = `fu-${r.userId}`;
                    const userName = r.user ? `${r.user.firstName} ${r.user.lastName}` : 'Unassigned';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{userName}</td>
                          <td className="px-4 py-3 text-gray-700">{r.noFollowup}</td>
                          <td className="px-4 py-3 text-gray-700">{r.first}</td>
                          <td className="px-4 py-3 text-gray-700">{r.second}</td>
                          <td className="px-4 py-3 text-gray-700">{r.thirdPlus}</td>
                          <DrillTrigger
                            count={r.totalLeads}
                            active={expanded?.key === rowKey}
                            drillable={!!r.userId}
                            className="font-semibold"
                            onToggle={() => toggle(rowKey, `${userName} — Total Leads`, { assignedTo: r.userId })}
                          />
                        </tr>
                        {expanded?.key === rowKey && panelProps && <DrillDownPanel colSpan={6} {...panelProps} />}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Overdue/Pending table */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Overdue / Pending Leads</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Sales Executive</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Day 1</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Day 2</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Day 3-5</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Day 6+</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Total Overdue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => {
                    const rowKey = `od-${r.userId}`;
                    const userName = r.user ? `${r.user.firstName} ${r.user.lastName}` : 'Unassigned';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{userName}</td>
                          <td className="px-4 py-3 text-gray-700">{r.day1}</td>
                          <td className="px-4 py-3 text-gray-700">{r.day2}</td>
                          <td className="px-4 py-3 text-gray-700">{r.day3to5}</td>
                          <td className="px-4 py-3 text-red-600 font-medium">{r.day6plus}</td>
                          <DrillTrigger
                            count={r.totalOverdues}
                            active={expanded?.key === rowKey}
                            drillable={!!r.userId}
                            className="font-semibold"
                            onToggle={() => toggle(rowKey, `${userName} — Overdue Leads`, { assignedTo: r.userId, overdue: 'true' })}
                          />
                        </tr>
                        {expanded?.key === rowKey && panelProps && <DrillDownPanel colSpan={6} {...panelProps} />}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
